import React, { useContext } from "react";
import { isUndefined } from "lodash";
import { useHistory } from "react-router";
import { FEATURE_FLAG_NAME } from "common/constants";
import { useDeleteSubmissionMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { CommonModal } from "../../Common/CommonModal";
import { Body } from "../../Common/Typography";

export type DeleteConfirmationProps = {
  submission: {
    id: string;
    property?: Maybe<{
      id: string;
    }>;
    submissionTypeVersion: {
      submissionType: { name: string; category: string };
    };
  };
  prevLocation?: string;
  closeModal: () => void;
  onUpdate?: () => void;
};

const COPY_WITHOUT_TASKS_TEXT =
  "Are you sure you would like to delete this record? All associated files will also be deleted from your internal dashboard and public website.";
const COPY_WITH_TASKS_TEXT =
  "Are you sure you would like to delete this record? All associated files and tasks will also be deleted from your internal dashboard and public website.";

const DeleteConfirmation = ({
  submission,
  prevLocation,
  closeModal,
  onUpdate,
}: DeleteConfirmationProps) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const history = useHistory();
  const { isFeatureEnabled } = useContext(AuthContext);

  const [deleteSubmission, { loading }] = useDeleteSubmissionMutation({
    onCompleted: () => {
      track("Submission deleted", {
        submissionName: submission.submissionTypeVersion.submissionType.name,
        propertyId: submission.property?.id,
      });
      onUpdate?.();
      addSuccessToast("Your submission was successfully deleted");
      if (!isUndefined(prevLocation)) {
        history.push(prevLocation);
      }
      closeModal();
    },
    onError: () => {
      closeModal();
      addErrorToast(
        "There was an issue deleting this submission. Please try again. If the problem persists, please email us at support@withforerunner.com",
      );
    },
  });

  const bodyText =
    isFeatureEnabled(FEATURE_FLAG_NAME.TASKS_WEB) ? COPY_WITH_TASKS_TEXT : (
      COPY_WITHOUT_TASKS_TEXT
    );

  return (
    <CommonModal
      title="Delete record"
      subtitle={submission.submissionTypeVersion.submissionType.name}
      content={
        <div>
          <Body size="large" type="regular">
            {bodyText}{" "}
            <Body size="large" type="emphasis" as="span">
              This action cannot be undone.
            </Body>
          </Body>
        </div>
      }
      onCancel={closeModal}
      onSubmit={async () => {
        await deleteSubmission({ variables: { submissionId: submission.id } });
      }}
      loading={loading}
      size="small"
      primaryButtonText="Delete"
      variant="destructive"
    />
  );
};

export default DeleteConfirmation;
